import React, { useState, useEffect } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { SubscriptionCard } from '@/components/SubscriptionCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Power, RefreshCw, Building2, DollarSign, Calendar, TrendingUp, Download, FolderTree } from 'lucide-react';
import { NumericFormat } from 'react-number-format';

const CATEGORIAS = [
  'Pessoas',
  'Estrutura',
  'Ferramentas/Softwares',
  'Impostos fixos',
  'Financeiro',
  'Veículos',
  'Marketing fixo',
  'Outros'
];

const CustosFixosRecorrentes = ({ user, onLogout }) => {
  const [custos, setCustos] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [editingCusto, setEditingCusto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [gerando, setGerando] = useState(false);
  const [importando, setImportando] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [selectedForImport, setSelectedForImport] = useState([]);
  const [filtroTipo, setFiltroTipo] = useState('todas');

  const [formData, setFormData] = useState({
    descricao: '',
    categoria: 'Estrutura',
    tipo: 'fixa',
    valor: '',
    tipo_recorrencia: 'mensal',
    dia_vencimento: '10',
    centro_custo: '',
    num_parcelas: '',
    parcela_atual: '0',
    antecipar_geracao: false,
    dia_geracao_antecipada: '',
  });

  const company = JSON.parse(localStorage.getItem('company') || '{}');

  useEffect(() => {
    fetchCustos();
  }, []);

  const fetchCustos = async () => {
    if (!company.id) return;
    try {
      const response = await axiosInstance.get(`/custos-fixos/${company.id}`);
      setCustos(response.data);
    } catch (error) {
      toast.error('Erro ao carregar custos fixos');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = {
        empresa_id: company.id,
        ...formData,
        valor: parseFloat(formData.valor),
        dia_vencimento: parseInt(formData.dia_vencimento),
        num_parcelas: formData.num_parcelas ? parseInt(formData.num_parcelas) : null,
        parcela_atual: formData.tipo_recorrencia === 'parcelado' ? parseInt(formData.parcela_atual || '0') : null,
        dia_geracao_antecipada: formData.antecipar_geracao && formData.dia_geracao_antecipada
          ? parseInt(formData.dia_geracao_antecipada)
          : null,
      };

      if (editingCusto) {
        await axiosInstance.put(`/custos-fixos/${editingCusto.id}`, data);
        toast.success('Despesa recorrente atualizada!');
      } else {
        await axiosInstance.post('/custos-fixos', data);
        toast.success('Despesa recorrente criada!');
      }

      setShowDialog(false);
      resetForm();
      fetchCustos();
    } catch (error) {
      toast.error('Erro ao salvar despesa recorrente');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (custo) => {
    setEditingCusto(custo);
    setFormData({
      descricao: custo.descricao,
      categoria: custo.categoria,
      tipo: custo.tipo || 'fixa',
      valor: custo.valor.toString(),
      tipo_recorrencia: custo.tipo_recorrencia,
      dia_vencimento: custo.dia_vencimento.toString(),
      centro_custo: custo.centro_custo || '',
      num_parcelas: custo.num_parcelas?.toString() || '',
      parcela_atual: custo.parcela_atual?.toString() || '0',
      antecipar_geracao: custo.antecipar_geracao || false,
      dia_geracao_antecipada: custo.dia_geracao_antecipada?.toString() || '',
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Tem certeza que deseja excluir esta despesa recorrente?')) return;

    try {
      await axiosInstance.delete(`/custos-fixos/${id}`);
      toast.success('Despesa recorrente excluída!');
      fetchCustos();
    } catch (error) {
      toast.error('Erro ao excluir despesa recorrente');
    }
  };

  const handleToggleStatus = async (custo) => {
    try {
      const novoStatus = custo.status === 'ativo' ? 'inativo' : 'ativo';
      await axiosInstance.patch(`/custos-fixos/${custo.id}/status`, { status: novoStatus });
      toast.success(`Status alterado para ${novoStatus}`);
      fetchCustos();
    } catch (error) {
      toast.error('Erro ao alterar status');
    }
  };

  const handleGerarContas = async () => {
    setGerando(true);
    try {
      const mes = new Date().toISOString().slice(0, 7);
      const response = await axiosInstance.post(`/gps-financeiro/gerar-contas-pagar/${company.id}?mes=${mes}`);
      
      if (response.data.contas_geradas > 0) {
        toast.success(`${response.data.contas_geradas} conta(s) a pagar gerada(s) para ${mes}`);
      } else if (response.data.contas_existentes > 0) {
        toast.info(`Contas já foram geradas para ${mes}`);
      } else {
        toast.info('Nenhuma despesa recorrente ativa para gerar');
      }
    } catch (error) {
      toast.error('Erro ao gerar contas a pagar');
    } finally {
      setGerando(false);
    }
  };

  // Funções de Importação do Plano de Contas
  const handleOpenImport = async () => {
    try {
      const response = await axiosInstance.get(`/custos-fixos/importar/preview/${company.id}`);
      setImportPreview(response.data);
      setSelectedForImport([]);
      setShowImportDialog(true);
    } catch (error) {
      toast.error('Erro ao carregar categorias do Plano de Contas');
    }
  };

  const handleToggleImportItem = (id) => {
    setSelectedForImport(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const handleSelectAllImport = () => {
    if (selectedForImport.length === importPreview?.disponiveis?.length) {
      setSelectedForImport([]);
    } else {
      setSelectedForImport(importPreview?.disponiveis?.map(c => c.id) || []);
    }
  };

  const handleConfirmImport = async () => {
    if (selectedForImport.length === 0) {
      toast.error('Selecione pelo menos uma categoria para importar');
      return;
    }

    setImportando(true);
    try {
      const response = await axiosInstance.post(`/custos-fixos/importar/${company.id}`, {
        categorias_ids: selectedForImport
      });
      
      toast.success(response.data.mensagem);
      setShowImportDialog(false);
      fetchCustos();
    } catch (error) {
      toast.error('Erro ao importar categorias');
    } finally {
      setImportando(false);
    }
  };

  const resetForm = () => {
    setEditingCusto(null);
    setFormData({
      descricao: '',
      categoria: 'Estrutura',
      tipo: 'fixa',
      valor: '',
      tipo_recorrencia: 'mensal',
      dia_vencimento: '10',
      centro_custo: '',
      num_parcelas: '',
      parcela_atual: '0',
      antecipar_geracao: false,
      dia_geracao_antecipada: '',
    });
  };

  // Cálculos resumo
  const parceladoQuitado = (c) => c.tipo_recorrencia === 'parcelado' && (c.parcela_atual || 0) >= (c.num_parcelas || 1);

  // Contribuição mensal de um custo: anual rateia /12, parcelado rateia /num_parcelas
  // e para de contar quando já quitado, demais tipos contam o valor cheio.
  const valorMensalDoCusto = (c) => {
    if (c.tipo_recorrencia === 'anual') return c.valor / 12;
    if (c.tipo_recorrencia === 'parcelado') {
      if (parceladoQuitado(c)) return 0;
      return c.valor / (c.num_parcelas || 1);
    }
    return c.valor;
  };

  const custosAtivos = custos.filter(c => c.status === 'ativo');
  const custosFixosAtivos = custosAtivos.filter(c => (c.tipo || 'fixa') === 'fixa');
  const custosVariaveisAtivos = custosAtivos.filter(c => c.tipo === 'variavel');
  const totalMensal = custosAtivos.reduce((acc, c) => acc + valorMensalDoCusto(c), 0);
  const totalMensalFixas = custosFixosAtivos.reduce((acc, c) => acc + valorMensalDoCusto(c), 0);
  const totalMensalVariaveis = custosVariaveisAtivos.reduce((acc, c) => acc + valorMensalDoCusto(c), 0);

  const custosPorCategoria = CATEGORIAS.map(cat => ({
    categoria: cat,
    total: custosAtivos.filter(c => c.categoria === cat).reduce((acc, c) => acc + valorMensalDoCusto(c), 0)
  })).filter(c => c.total > 0);

  const custosExibidos = custos
    .filter(c => {
      if (filtroTipo === 'todas') return true;
      return (c.tipo || 'fixa') === filtroTipo;
    })
    .sort((a, b) => a.descricao.localeCompare(b.descricao, 'pt-BR'));

  return (
    <div className="flex min-h-screen">
      <Sidebar user={user} onLogout={onLogout} />
      
      <div className="flex-1 p-8">
        <SubscriptionCard user={user} />

        {/* Header */}
        <div className="mb-8 flex justify-between items-start">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">Despesas Recorrentes</h1>
            <p className="text-gray-400">
              Despesas fixas e variáveis mensais da empresa (custo de existência)
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={handleOpenImport}
              variant="outline"
              className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
            >
              <FolderTree className="mr-2" size={18} />
              Importar do Plano de Contas
            </Button>
            <Button
              onClick={handleGerarContas}
              disabled={gerando}
              variant="outline"
              className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
            >
              <RefreshCw className={`mr-2 ${gerando ? 'animate-spin' : ''}`} size={18} />
              {gerando ? 'Gerando...' : 'Gerar Contas do Mês'}
            </Button>
            <Button
              onClick={() => { resetForm(); setShowDialog(true); }}
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
            >
              <Plus className="mr-2" size={18} />
              Nova Despesa Recorrente
            </Button>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="glass border-white/10 hover-lift border-l-4 border-l-purple-500">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-400 flex items-center">
                <DollarSign className="mr-2" size={18} />
                Total Mensal
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-purple-400">
                R$ {totalMensal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {custosAtivos.length} despesa(s) ativa(s)
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Fixas: R$ {totalMensalFixas.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} · Variáveis: R$ {totalMensalVariaveis.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </CardContent>
          </Card>

          <Card className="glass border-white/10 hover-lift border-l-4 border-l-blue-500">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-400 flex items-center">
                <Calendar className="mr-2" size={18} />
                Total Anual
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-blue-400">
                R$ {(totalMensal * 12).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Projeção anual
              </p>
            </CardContent>
          </Card>

          <Card className="glass border-white/10 hover-lift border-l-4 border-l-cyan-500">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-400 flex items-center">
                <TrendingUp className="mr-2" size={18} />
                Custo Diário
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-cyan-400">
                R$ {(totalMensal / 30).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Base: 30 dias
              </p>
            </CardContent>
          </Card>

          <Card className="glass border-white/10 hover-lift border-l-4 border-l-emerald-500">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-400 flex items-center">
                <Building2 className="mr-2" size={18} />
                Categorias
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-emerald-400">
                {custosPorCategoria.length}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                com custos ativos
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Distribuição por Categoria */}
        {custosPorCategoria.length > 0 && (
          <Card className="glass border-white/10 mb-8">
            <CardHeader>
              <CardTitle className="text-white">Distribuição por Categoria</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {custosPorCategoria.map((cat) => (
                  <div key={cat.categoria} className="p-4 rounded-lg bg-white/5 border border-white/10">
                    <p className="text-sm text-gray-400">{cat.categoria}</p>
                    <p className="text-lg font-bold text-white">
                      R$ {cat.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                    <p className="text-xs text-gray-500">
                      {((cat.total / totalMensal) * 100).toFixed(1)}% do total
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabela de Custos */}
        <Card className="glass border-white/10">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-white">Lista de Despesas Recorrentes ({custosExibidos.length})</CardTitle>
            <Select value={filtroTipo} onValueChange={setFiltroTipo}>
              <SelectTrigger className="w-40 bg-white/5 border-white/10 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas</SelectItem>
                <SelectItem value="fixa">Fixas</SelectItem>
                <SelectItem value="variavel">Variáveis</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-white/5">
                  <TableHead className="text-gray-300">Descrição</TableHead>
                  <TableHead className="text-gray-300">Categoria</TableHead>
                  <TableHead className="text-gray-300 text-center">Tipo</TableHead>
                  <TableHead className="text-gray-300 text-right">Valor</TableHead>
                  <TableHead className="text-gray-300">Recorrência</TableHead>
                  <TableHead className="text-gray-300 text-center">Vencimento</TableHead>
                  <TableHead className="text-gray-300 text-center">Status</TableHead>
                  <TableHead className="text-gray-300 text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {custosExibidos.map((custo) => (
                  <TableRow key={custo.id} className="border-white/10 hover:bg-white/5">
                    <TableCell className="text-white font-medium">{custo.descricao}</TableCell>
                    <TableCell className="text-gray-400">{custo.categoria}</TableCell>
                    <TableCell className="text-center">
                      <Badge className={(custo.tipo || 'fixa') === 'fixa'
                        ? 'bg-purple-500/20 text-purple-400 border-purple-500/50'
                        : 'bg-orange-500/20 text-orange-400 border-orange-500/50'
                      }>
                        {(custo.tipo || 'fixa') === 'fixa' ? 'Fixa' : 'Variável'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-white text-right font-mono">
                      R$ {custo.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell className="text-gray-400 capitalize">
                      {custo.tipo_recorrencia}
                      {custo.tipo_recorrencia === 'parcelado' && ` (${custo.parcela_atual || 0}/${custo.num_parcelas || 1})`}
                    </TableCell>
                    <TableCell className="text-gray-400 text-center">
                      Dia {custo.dia_vencimento}
                      {custo.antecipar_geracao && custo.dia_geracao_antecipada && (
                        <Badge className="ml-1 bg-cyan-500/20 text-cyan-400 border-cyan-500/50">
                          Antecipa dia {custo.dia_geracao_antecipada}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {parceladoQuitado(custo) ? (
                        <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/50">Quitado</Badge>
                      ) : (
                        <Badge className={custo.status === 'ativo'
                          ? 'bg-green-500/20 text-green-400 border-green-500/50'
                          : 'bg-gray-500/20 text-gray-400 border-gray-500/50'
                        }>
                          {custo.status}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleToggleStatus(custo)}
                          className="text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/10"
                          title={custo.status === 'ativo' ? 'Desativar' : 'Ativar'}
                        >
                          <Power size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(custo)}
                          className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                        >
                          <Edit size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(custo.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {custosExibidos.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-gray-500 py-8">
                      Nenhuma despesa recorrente cadastrada. Clique em "Nova Despesa Recorrente" para começar.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Dialog de Criação/Edição */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="glass border-white/10 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">
                {editingCusto ? 'Editar Despesa Recorrente' : 'Nova Despesa Recorrente'}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Tipo de Despesa</Label>
                <RadioGroup
                  value={formData.tipo}
                  onValueChange={(value) => setFormData({ ...formData, tipo: value })}
                  className="grid grid-cols-2 gap-2"
                >
                  <label className="flex items-center gap-2 p-2 rounded-md border border-white/10 bg-white/5 cursor-pointer">
                    <RadioGroupItem value="fixa" id="tipo-fixa" />
                    <span className="text-white text-sm">Fixa</span>
                  </label>
                  <label className="flex items-center gap-2 p-2 rounded-md border border-white/10 bg-white/5 cursor-pointer">
                    <RadioGroupItem value="variavel" id="tipo-variavel" />
                    <span className="text-white text-sm">Variável</span>
                  </label>
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">Descrição *</Label>
                <Input
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  placeholder="Ex: Aluguel do escritório"
                  className="bg-white/5 border-white/10 text-white"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Categoria *</Label>
                  <Select
                    value={formData.categoria}
                    onValueChange={(value) => setFormData({ ...formData, categoria: value })}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIAS.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300">Valor (R$) *</Label>
                  <NumericFormat
                    value={formData.valor}
                    onValueChange={(values) => setFormData({ ...formData, valor: values.value })}
                    thousandSeparator="."
                    decimalSeparator=","
                    decimalScale={2}
                    fixedDecimalScale
                    allowNegative={false}
                    prefix="R$ "
                    placeholder="R$ 0,00"
                    className="flex h-9 w-full rounded-md border px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm bg-white/5 border-white/10 text-white"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">Recorrência</Label>
                <RadioGroup
                  value={formData.tipo_recorrencia}
                  onValueChange={(value) => setFormData({ ...formData, tipo_recorrencia: value })}
                  className="grid grid-cols-3 gap-2"
                >
                  <label className="flex items-center gap-2 p-2 rounded-md border border-white/10 bg-white/5 cursor-pointer">
                    <RadioGroupItem value="mensal" id="rec-mensal" />
                    <span className="text-white text-sm">Mensal</span>
                  </label>
                  <label className="flex items-center gap-2 p-2 rounded-md border border-white/10 bg-white/5 cursor-pointer">
                    <RadioGroupItem value="anual" id="rec-anual" />
                    <span className="text-white text-sm">Anual</span>
                  </label>
                  <label className="flex items-center gap-2 p-2 rounded-md border border-white/10 bg-white/5 cursor-pointer">
                    <RadioGroupItem value="parcelado" id="rec-parcelado" />
                    <span className="text-white text-sm">Parcelado</span>
                  </label>
                </RadioGroup>
                {formData.tipo_recorrencia === 'anual' && (
                  <p className="text-xs text-gray-500">O valor será dividido por 12 para gerar a conta a pagar de cada mês.</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Dia de Vencimento</Label>
                  <Input
                    type="number"
                    min="1"
                    max="31"
                    value={formData.dia_vencimento}
                    onChange={(e) => setFormData({ ...formData, dia_vencimento: e.target.value })}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-2 p-2 rounded-md border border-white/10 bg-white/5 cursor-pointer w-fit">
                  <Checkbox
                    checked={formData.antecipar_geracao}
                    onCheckedChange={(checked) => setFormData({ ...formData, antecipar_geracao: checked === true })}
                  />
                  <span className="text-white text-sm">Lançar antes da virada do mês?</span>
                </label>
                {formData.antecipar_geracao && (
                  <div className="pt-2">
                    <Label className="text-gray-300">Dia do lançamento antecipado (mês atual)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="28"
                      value={formData.dia_geracao_antecipada}
                      onChange={(e) => setFormData({ ...formData, dia_geracao_antecipada: e.target.value })}
                      placeholder="Ex: 25"
                      className="bg-white/5 border-white/10 text-white"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      A conta do mês seguinte será lançada nesse dia do mês atual, antes da virada. O vencimento continua sendo o "Dia de Vencimento" acima, já no mês seguinte.
                    </p>
                  </div>
                )}
              </div>

              {formData.tipo_recorrencia === 'parcelado' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-300">Número de Parcelas</Label>
                    <Input
                      type="number"
                      min="2"
                      value={formData.num_parcelas}
                      onChange={(e) => setFormData({ ...formData, num_parcelas: e.target.value })}
                      placeholder="Ex: 12"
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300">Parcela Atual</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.parcela_atual}
                      onChange={(e) => setFormData({ ...formData, parcela_atual: e.target.value })}
                      placeholder="Ex: 6 (se já pagou 6 de 10)"
                      className="bg-white/5 border-white/10 text-white"
                    />
                    <p className="text-xs text-gray-500">Quantas parcelas já foram pagas até agora.</p>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-gray-300">Centro de Custo (opcional)</Label>
                <Input
                  value={formData.centro_custo}
                  onChange={(e) => setFormData({ ...formData, centro_custo: e.target.value })}
                  placeholder="Ex: Administrativo"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowDialog(false)}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-gradient-to-r from-purple-600 to-blue-600"
                >
                  {loading ? 'Salvando...' : (editingCusto ? 'Atualizar' : 'Criar')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog de Importação do Plano de Contas */}
        <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
          <DialogContent className="glass border-white/10 max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <FolderTree className="text-emerald-400" size={24} />
                Importar do Plano de Contas
              </DialogTitle>
              <DialogDescription className="text-gray-400">
                Selecione as categorias do Plano de Contas para importar como Custos Fixos.
                Os valores serão importados como R$ 0,00 - você precisará preenchê-los.
              </DialogDescription>
            </DialogHeader>

            {importPreview && (
              <div className="flex-1 overflow-auto space-y-4">
                {/* Categorias Disponíveis */}
                {importPreview.disponiveis.length > 0 ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-400">
                        {importPreview.total_disponiveis} categoria(s) disponível(is) para importação
                      </p>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleSelectAllImport}
                        className="text-emerald-400 hover:text-emerald-300"
                      >
                        {selectedForImport.length === importPreview.disponiveis.length 
                          ? 'Desmarcar todos' 
                          : 'Selecionar todos'}
                      </Button>
                    </div>

                    <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
                      {importPreview.disponiveis.map((cat) => (
                        <div
                          key={cat.id}
                          onClick={() => handleToggleImportItem(cat.id)}
                          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                            selectedForImport.includes(cat.id)
                              ? 'bg-emerald-500/20 border-emerald-500/50'
                              : 'bg-white/5 border-white/10 hover:bg-white/10'
                          }`}
                        >
                          <Checkbox
                            checked={selectedForImport.includes(cat.id)}
                            onCheckedChange={() => handleToggleImportItem(cat.id)}
                          />
                          <div className="flex-1">
                            <p className="text-white font-medium">{cat.name}</p>
                            <p className="text-xs text-gray-500">{cat.description}</p>
                          </div>
                          <Badge className={
                            cat.group === 'FIXA' 
                              ? 'bg-purple-500/20 text-purple-400 border-purple-500/50'
                              : 'bg-orange-500/20 text-orange-400 border-orange-500/50'
                          }>
                            {cat.group === 'FIXA' ? 'Fixa' : 'Variável Indireta'}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <FolderTree className="mx-auto mb-2 opacity-50" size={32} />
                    <p>Todas as categorias já foram importadas!</p>
                  </div>
                )}

                {/* Já Importadas */}
                {importPreview.ja_importadas.length > 0 && (
                  <div className="space-y-2 pt-4 border-t border-white/10">
                    <p className="text-sm text-gray-500">
                      {importPreview.total_ja_importadas} categoria(s) já importada(s):
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {importPreview.ja_importadas.map((cat) => (
                        <Badge key={cat.id} variant="outline" className="text-gray-500 border-white/20">
                          {cat.name} ✓
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <DialogFooter className="pt-4 border-t border-white/10">
              <Button
                variant="outline"
                onClick={() => setShowImportDialog(false)}
                className="border-white/20 text-white hover:bg-white/10"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmImport}
                disabled={importando || selectedForImport.length === 0}
                className="bg-gradient-to-r from-emerald-600 to-cyan-600"
              >
                {importando ? 'Importando...' : `Importar ${selectedForImport.length} Selecionado(s)`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default CustosFixosRecorrentes;
